import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardBody } from '@vnetwork/ui/primitives';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useJourneyNegotiations,
  useAddJourneyNote,
  useDeleteJourneyNote,
  useConvertPocToOfficial,
  type CustomerJourney,
  type JourneyNote,
} from '@/api/customer-journey';
import { errorMessage } from '../sale-process-error';
import { AddJourneyNoteDialog } from '../add-journey-note-dialog';
import { CreateNegotiationDialog } from '../create-negotiation-dialog';
import { StageColumn, type StageColumnData } from './stage-column';
import { JourneyBoardHeader } from './journey-board-header';
import {
  BOARD_STEPS,
  LEGACY_APP_BASE,
  legacyUrl,
  journeyOutcome,
  currentStageIndex,
  stageStatus,
  stepNotes,
  pocNotes,
  businessNotes,
  ticketNotes,
  contractServiceNotes,
  hasCompletedContract,
} from './board-helpers';

/**
 * The interactive Pipeline board for the active journey — a 1-1 rebuild of the
 * legacy SaleProcessBoard kanban (4 pipeline columns, each with its notes,
 * step-specific sections, and action rail). Fully-backed interactions run
 * in-app (move step, add note/negotiation, delete note, convert POC); the
 * create-flows whose modules the workspace doesn't own yet (POC / business trip
 * / meeting) open the legacy app; Create contract routes to the in-app wizard.
 */
export function JourneyStageBoard({
  customerId,
  journey,
  isUpsell = false,
}: {
  customerId: number;
  journey: CustomerJourney;
  isUpsell?: boolean;
}) {
  const toast = useToast();
  const navigate = useNavigate();
  const { data: negData } = useJourneyNegotiations(journey.customer_journey_id);
  const moveNote = useAddJourneyNote(customerId, journey.customer_journey_id);
  const deleteNote = useDeleteJourneyNote(customerId, journey.customer_journey_id);
  const convertPoc = useConvertPocToOfficial(customerId);

  const [pinnedStep, setPinnedStep] = useState('searching');
  const [noteOpen, setNoteOpen] = useState(false);
  const [negOpen, setNegOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<JourneyNote | null>(null);
  const [convertTarget, setConvertTarget] = useState<JourneyNote | null>(null);
  const [convertingPocId, setConvertingPocId] = useState<number | null>(null);

  const outcome = journeyOutcome(journey);
  const interactive = outcome === 'active';
  const locked = !!journey.customer_journey_locked;
  const currentIdx = currentStageIndex(journey);
  const jid = journey.customer_journey_id;
  const contactId = journey.contact_id ?? 0;

  const notes = journey.customer_journey_notes ?? [];
  const negotiations = negData?.results ?? [];
  const contractDone = hasCompletedContract(notes);

  const columns: StageColumnData[] = useMemo(
    () =>
      BOARD_STEPS.map((s, index) => ({
        step: s.value,
        label: s.label,
        index,
        status: stageStatus(index, currentIdx, outcome),
        notes: stepNotes(notes, s.value),
        pocs: s.value === 'poc' ? pocNotes(notes) : [],
        businesses: s.value === 'presentation' ? businessNotes(notes) : [],
        tickets: s.value === 'presentation' ? ticketNotes(notes) : [],
        negotiations: negotiations.filter(
          (n) => (n.contract_negotiation_customer_journey_step || 'contract') === s.value,
        ),
        contractServices: s.value === 'contract' ? contractServiceNotes(notes) : [],
      })),
    [notes, negotiations, currentIdx, outcome],
  );

  const stepLabelOf = (step: string) => BOARD_STEPS.find((s) => s.value === step)?.label ?? step;

  const moveToStep = (step: string) => {
    if (!interactive || step === journey.customer_journey_step) return;
    moveNote.mutate(
      {
        customer_journey_note: `Moved to ${stepLabelOf(step)}`,
        customer_journey_note_step: step,
        customer_journey_note_step_status: 'processing',
        customer_journey_notes_time: new Date().toISOString(),
      },
      {
        onSuccess: () => toast.success(`Moved to ${stepLabelOf(step)}`),
        onError: (e) => toast.error('Failed to move step', { description: errorMessage(e) }),
      },
    );
  };
  const moveIfNeeded = (step: string) => {
    if (interactive && step !== journey.customer_journey_step) moveToStep(step);
  };

  const openExternal = (url: string) => window.open(url, '_blank', 'noopener');
  const createBusinessTrip = () => {
    moveIfNeeded('presentation');
    openExternal(legacyUrl('/account/businesses/new', { customer_id: customerId, customer_journey_id: jid }));
  };
  const createMeeting = () => {
    moveIfNeeded('presentation');
    openExternal(
      legacyUrl('/tickets/new', {
        customer_id: customerId,
        customer_journey_id: jid,
        ticket_type: 'meeting',
        ticket_meeting_type: 'online',
      }),
    );
  };
  const createPOC = () => {
    moveIfNeeded('poc');
    openExternal(legacyUrl(`/customers/${customerId}/poc`, { customer_id: customerId, customer_journey_id: jid, contact_id: contactId }));
  };
  const createContract = () => {
    moveIfNeeded('contract');
    navigate(`/contracts/new?customer_id=${customerId}&customer_journey_id=${jid}&contact_id=${contactId}`);
  };
  const openPocService = (note: JourneyNote) =>
    openExternal(`${LEGACY_APP_BASE}/services/${note.service_poc?.service_id}`);

  const runDeleteNote = () => {
    if (!deleteTarget?.customer_journey_note_id) return;
    deleteNote.mutate(deleteTarget.customer_journey_note_id, {
      onSuccess: () => {
        toast.success('Note deleted');
        setDeleteTarget(null);
      },
      onError: (e) => toast.error('Failed to delete note', { description: errorMessage(e) }),
    });
  };
  const runConvertPoc = () => {
    const id = Number(convertTarget?.service_poc?.service_id);
    if (!id) return;
    setConvertingPocId(id);
    convertPoc.mutate(id, {
      onSuccess: () => {
        toast.success('POC converted to official service');
        setConvertTarget(null);
      },
      onError: (e) => toast.error('Failed to convert POC', { description: errorMessage(e) }),
      onSettled: () => setConvertingPocId(null),
    });
  };

  return (
    <Card flat>
      <CardHeader>
        <CardTitle>Pipeline board</CardTitle>
      </CardHeader>
      <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <JourneyBoardHeader customerId={customerId} journey={journey} outcome={outcome} isUpsell={isUpsell} />

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${BOARD_STEPS.length}, minmax(220px, 1fr))`,
            gap: 'var(--vn-space-3)',
            overflowX: 'auto',
            alignItems: 'start',
          }}
        >
          {columns.map((data) => (
            <StageColumn
              key={data.step}
              data={data}
              interactive={interactive}
              locked={locked}
              hasCompletedContract={contractDone}
              convertingPocId={convertingPocId}
              onMoveToStep={moveToStep}
              onAddNote={(step) => {
                setPinnedStep(step);
                setNoteOpen(true);
              }}
              onAddNegotiation={(step) => {
                setPinnedStep(step);
                setNegOpen(true);
              }}
              onDeleteNote={setDeleteTarget}
              onConvertPoc={setConvertTarget}
              onUpdatePocResult={openPocService}
              onExtendPoc={openPocService}
              onCreateBusinessTrip={createBusinessTrip}
              onCreateMeeting={createMeeting}
              onCreatePOC={createPOC}
              onCreateContract={createContract}
            />
          ))}
        </div>
      </CardBody>

      <AddJourneyNoteDialog
        key={`note-${pinnedStep}`}
        customerId={customerId}
        journey={journey}
        defaultStep={pinnedStep}
        open={noteOpen}
        onOpenChange={setNoteOpen}
      />
      <CreateNegotiationDialog
        key={`neg-${pinnedStep}`}
        customerId={customerId}
        journey={journey}
        step={pinnedStep}
        open={negOpen}
        onOpenChange={setNegOpen}
        onCreated={(ok, detail) =>
          ok ? toast.success('Negotiation created') : toast.error('Failed to create negotiation', { description: detail })
        }
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(o) => (o ? undefined : setDeleteTarget(null))}
        title="Delete note"
        description="Remove this note from the journey. This cannot be undone."
        confirmLabel="Delete"
        destructive
        loading={deleteNote.isPending}
        onConfirm={runDeleteNote}
      />
      <ConfirmDialog
        open={!!convertTarget}
        onOpenChange={(o) => (o ? undefined : setConvertTarget(null))}
        title="Convert POC to official service"
        description={`Promote trial service ${convertTarget?.service_poc?.service_id ?? ''} · ${convertTarget?.service_poc?.service_name ?? ''} into a billable official service.`}
        confirmLabel="Convert"
        loading={convertPoc.isPending}
        onConfirm={runConvertPoc}
      />
    </Card>
  );
}

export default JourneyStageBoard;
